import { Compass, Zap, UserPlus, Target, BarChart3, Clock } from "lucide-react";
import Reveal from "./Reveal";

const USERS_IMG =
  "https://images.unsplash.com/photo-1450044804117-534ccd6e6a3a?q=80&w=1400&auto=format&fit=crop";
const PROMOTERS_IMG =
  "https://images.unsplash.com/photo-1542628682-88321d2a4828?q=80&w=1400&auto=format&fit=crop";

const PANELS = [
  {
    id: "users",
    tag: "For stagehands & crew",
    title: "Never miss what's on.",
    img: USERS_IMG,
    alt: "Concert crowd with hands raised",
    items: [
      { icon: Compass, text: "Effortless discovery in a mobile-first, personalized feed tailored to your interests and location." },
      { icon: Zap, text: "Trending events, location-based picks and artist & venue info. Everything you need to be there." },
      { icon: UserPlus, text: "A social layer to invite friends, coordinate plans and share the experience." },
    ],
  },
  {
    id: "promoters",
    tag: "For production companies & venues",
    title: "Reach the right audience.",
    img: PROMOTERS_IMG,
    alt: "Crowd in front of the DJ booth",
    items: [
      { icon: Target, text: "Put your events in front of targeted audiences with zero friction: the right people at the right time, every time." },
      { icon: BarChart3, text: "Actionable insights into audience behavior and event performance." },
      { icon: Clock, text: "Smarter promotions and growth, powered by data legacy platforms never gave independent venues." },
    ],
  },
];

export default function Sides() {
  return (
    <section id="experience" className="relative py-24 lg:py-32">
      <div
        className="pointer-events-none absolute left-[-14%] bottom-0 h-[540px] w-[540px] rounded-full bg-brand-dark opacity-60 blur-[160px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <span className="w-max rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">
            Two sides, one platform
          </span>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            Connecting people <span className="text-gradient">to moments.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-2" data-testid="sides-grid">
          {PANELS.map((panel, pi) => (
            <Reveal key={panel.id} delay={pi * 0.1}>
              <article
                className="group overflow-hidden rounded-3xl border border-brand-purple/20 bg-brand-card/80 backdrop-blur-xl transition-colors duration-500 hover:border-brand-purple/55"
                data-testid={`sides-panel-${panel.id}`}
              >
                <div className="relative h-56 overflow-hidden sm:h-64">
                  <img
                    src={panel.img}
                    alt={panel.alt}
                    className="h-full w-full object-cover opacity-70 saturate-[0.85] transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-card via-[#1a0640]/30 to-brand-purple/15" />
                  <div className="absolute bottom-4 left-6">
                    <span className="rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold backdrop-blur-md">
                      {panel.tag}
                    </span>
                  </div>
                </div>
                <div className="p-7 lg:p-9">
                  <h3 className="font-display text-2xl font-bold text-brand-paper lg:text-3xl">
                    {panel.title}
                  </h3>
                  <ul className="mt-6 space-y-4">
                    {panel.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3.5">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-brand-teal/25 bg-brand-teal/10 text-brand-teal">
                          <item.icon className="h-4 w-4" />
                        </span>
                        <span className="text-sm leading-relaxed text-brand-mist/75">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
